import { useEffect, useState, memo } from "react";
import {
  Box,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { DateFormater, formatAmount, handleSessionOut, Loader } from "./HelperFunctions";
import { useOutletContext, useRouteLoaderData } from "react-router-dom";
import CryptoJS from "crypto-js";
import { getToken } from "../API/tokenStore";
import ErrorIcon from "@mui/icons-material/Error";
import { fileStatus } from "../API/backendAPI";

const DsCardsTable = ({ date, onCellClick, setLoading, userPreference }) => {
  const { selectedCorp, restartSSE, setRestartSSE } = useOutletContext();
  const { userId, clientId } = useRouteLoaderData("reconciliation");
  const token = getToken();
  const [dsCardsGridData, setDsCardsGridData] = useState([]);

  const SECRET = "12345678901234567890123456789012";
  const encrypted = CryptoJS.AES.encrypt(token, SECRET).toString();

const columns = [
    { dataKey: "corporation", columnName: "Corporation", width: "110px" },
    { dataKey: "pc", columnName: "Profit Center", width: "80px" },
    { dataKey: "open_receipts", columnName: "Open Receipts", width: "100px" },
    {
      dataKey: "merchant_provider",
      columnName: "Merchant Provider",
      width: "120px",
    },
    { dataKey: "bank_account", columnName: "Bank Account", width: "120px" },
    { dataKey: "open_batches", columnName: "Open Batches", width: "100px" },
    { dataKey: "last_imported", columnName: "Last Imported", width: "120px" },
    { dataKey: "chargebacks", columnName: "Chargebacks", width: "100px" },
  ];

  const pcID = dsCardsGridData[0]?.pc_id;

  useEffect(() => {
    const pcID = dsCardsGridData?.[0]?.pc_id;

    // console.log("psidddd", pcID);

    if (pcID || pcID == null || pcID == '') {
      sessionStorage.setItem("pc_id", pcID);
    }
  }, [dsCardsGridData]);


  const shouldHideProfitCenter =
    dsCardsGridData.length > 0 &&
    dsCardsGridData.every(
      (row) => !row.pc || row.pc === "NA"
    );

  const filteredColumns = shouldHideProfitCenter
    ? columns.filter((col) => col.dataKey !== "pc")
    : columns;


  useEffect(() => {
    setLoading(true);
    const corp_id = selectedCorp?.ID === "select" ? "" : selectedCorp?.ID;

    const as_of_date = DateFormater(date);
    const dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\d{4}$/;

    if (!dateRegex.test(as_of_date)) {
      setLoading(false);
      return

    };

    const user_Preference = userPreference.IsShowCorpLegalName === "true";

    const url = `${process.env.REACT_APP_BACKEND_API_URL
      }/API/reconciliation_summary?client_id=${clientId}&user_id=${userId}&corp_id=${corp_id}&as_of_date=${as_of_date}&token=${encodeURIComponent(
        encrypted
      )}&userPreference=${user_Preference}`;
    const eventSource = new EventSource(url);

    eventSource.onmessage = (event) => {
      try {
        const parsedData = JSON.parse(event.data);
        if (parsedData?.status === 401) {
          handleSessionOut();
          return;
        }
        setDsCardsGridData(parsedData);
        setLoading(false);
      } catch (e) {
        console.error("Invalid SSE data:", e);
        setLoading(false);
      }
    };

    eventSource.onerror = (error) => {
      enqueueSnackbar("Error while getting DS Card data", { variant: "error" });
      setLoading(false);
      eventSource.close();
      // setRestartSSE(prev => !prev);
    };

    return () => {
      setLoading(false);
      eventSource.close();
    };
  }, [selectedCorp, date, restartSSE]);


  const RenderOpenReceipts = ({ data }) => (
    <Stack direction="row" width="100%" height="100%">
      <Stack width="100%" alignItems="flex-end" gap={2} mr={2}>
        <Typography noWrap fontSize="11px" fontWeight={500}>
          {data.open_receipts.count}
        </Typography>
        <Typography noWrap fontWeight={500}>
          $ {formatAmount(data.open_receipts.amount)}
        </Typography>
      </Stack>
    </Stack>
  );

  const Merchants = ({ data, field }) => {
    const isClickable = field === "open_batches" || field === "chargebacks";

    const handleErrorClick = async (item) => {
      const confirmed = window.confirm(
        item?.message || "Import failed. Do you want to continue?"
      );

      if (confirmed) {
        const updateFileStatus = await fileStatus(item.id);

        enqueueSnackbar(
          String(updateFileStatus?.data?.message || "Unable to update file status"),
          {
            variant: updateFileStatus?.success ? "success" : "error",
          }
        );
      } else {
        console.log("User clicked Cancel");
      }
    };

    return (
      <Box width="100%" height="100%">
        {data.merchants.map((item, index) => {
          let value = item[field];
          let color = "#626262";

          if (
            value &&
            typeof value === "object" &&
            "count" in value &&
            "amount" in value
          ) {
            value = `$${formatAmount(value.amount)} (${value.count})`;
          }
          if (field === "last_imported") {
            if (item.status === 0) {
              value = "In Progress";
              color = "#f39c12";
            } else if (item.status === -1) {
              value = <ErrorIcon onClick={() => handleErrorClick(item)} sx={{ cursor: "pointer" }} color="error" fontSize="small" />;
            } else {
              value = item.last_imported || "-";
            }
          }

          return (
            <Box key={index}>
              <Box
                onClick={
                  isClickable
                    ? () => onCellClick(data.corporation_id, item, field)
                    : undefined
                }
                sx={{
                  cursor: isClickable ? "pointer" : "default",
                  minHeight: "37px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  fontSize={12}
                  fontWeight={500}
                  color={color}
                  px={2.5}
                  py={1}
                  width="100%"
                  noWrap
                >
                  <span title={value}>
                    {value || "-"}
                  </span>
                </Typography>
              </Box>
              {index < data.merchants.length - 1 && <Divider />}
            </Box>
          );
        })}
      </Box>
    );
  };

  const ValueCell = memo(({ value }) => {
    const displayValue =
      typeof value === "object" && value !== null
        ? value.day || "NA"
        : value || "-";
    return (
      <Stack direction="row" width="100%" height="100%" p={1}>
        <Typography noWrap fontSize="12px" fontWeight={500}>
          <span title={displayValue}>
            {formatAmount(displayValue)}
          </span>
        </Typography>
      </Stack>
    );
  });

  return (
    <>
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          border: "1px solid #f2f2f2",
        }}
      >
        <Table stickyHeader size="small" sx={{ width: "100%" }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#d7d7d7" }}>
              {/* {columns.map((col) => ( */}
              {filteredColumns.map((col) => (
                <TableCell
                  key={col.dataKey}
                  style={{
                    backgroundColor: "#d7d7d7",
                    borderRight:
                      col.columnName === "Open Receipts"
                        ? "1px solid white"
                        : null,
                    fontSize: 13,
                    maxWidth: col.width,
                  }}
                >
                  {col.columnName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {dsCardsGridData.length === 0 ? (
              <TableRow>
                <TableCell
                  // colSpan={columns.length}
                  colSpan={filteredColumns.length}

                  align="center"
                  sx={{ fontSize: 14, color: "#999" }}
                >
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              dsCardsGridData.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {filteredColumns.map((col) => {
                    const isOpenReceipts = col.dataKey === "open_receipts";
                    const isOpenBatches = col.dataKey === "open_batches";
                    const isChargeBacks = col.dataKey === "chargebacks";
                    const isProfitCenter = col.dataKey === "pc";

                    return (
                      <TableCell
                        key={col.dataKey}
                        style={{
                          padding: "4px 0px 8px 0px",
                          fontSize: 12,
                          fontWeight: 500,
                          color: "#626262",
                          borderRight:
                            col.dataKey === "open_receipts"
                              ? "1px solid #d7d7d7"
                              : null,
                          maxWidth: col.width,
                          cursor:
                            isOpenBatches || isChargeBacks
                              ? "pointer"
                              : "default",
                          textAlign:
                            isOpenBatches || isChargeBacks ? "end" : undefined,
                        }}
                      // onClick={
                      //   isOpenReceipts
                      //     ? () =>
                      //         onCellClick(
                      //           row.corporation_id,
                      //           row,
                      //           col.dataKey
                      //         )
                      //     : undefined
                      // }
                      >
                        {
                          (() => {
                            switch (col.dataKey) {
                              case "open_receipts":
                                return <RenderOpenReceipts data={row} />;

                              case "corporation":
                              case "pc":
                                return <ValueCell value={row[col.dataKey]} />;

                              default:
                                return <Merchants data={row} field={col.dataKey} />;
                            }
                          })()
                        }

                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Stack>
    </>
  );
};

export default DsCardsTable;